import { useCallback, useEffect, useState } from "react";
import {
  exportSsdAudienceCsv,
  fetchSsdSales,
  ingestSsdSalesCsv,
  patchSsdShareEndCustomers,
  patchSsdGoogleConversionNames,
  patchSsdDestinationFeed,
  mapSsdDealer,
  acknowledgeSsdNewFacts,
  triggerCsvDownload,
  type SsdConsentLink,
  type SsdDealerMapBoard,
  type SsdExportJob,
  type SsdFeedState,
  type SsdGoogleConversions,
  type SsdDestinationFeed,
  type SsdImportJob,
  type SsdInboundSnapshot,
  type SsdQualityIssue,
  type SsdQualitySummary,
  type SsdSaleFact,
} from "@/lib/ssdSalesClient";
import type { OrgSalesAggregation } from "@/lib/ssd/ssdSalesAggregation";
import {
  GOOGLE_ADS_CONVERSION_NAME_DEFAULT,
  GOOGLE_ADS_CONVERSION_NAME_LEGACY,
} from "@/lib/ssd/ssdExportPreview";

const EMPTY_INBOUND: SsdInboundSnapshot = { events: [], by_type: [] };
const EMPTY_DEALER_MAP: SsdDealerMapBoard = {
  unmapped: [],
  mapped: [],
  subscribers: [],
  can_release: false,
  manufacturer_org_id: null,
  manufacturer_name: null,
};

const EMPTY_CONVERSIONS: SsdGoogleConversions = {
  names: [GOOGLE_ADS_CONVERSION_NAME_DEFAULT, GOOGLE_ADS_CONVERSION_NAME_LEGACY],
  selected: GOOGLE_ADS_CONVERSION_NAME_DEFAULT,
};

const EMPTY_AGGREGATION: OrgSalesAggregation = {
  retail: { sale_count: 0, gross_usd: 0 },
  manufacturing: { sale_count: 0, gross_usd: 0 },
  combined: { sale_count: 0, gross_usd: 0 },
  lens: "none",
};

const EMPTY_QUALITY: SsdQualitySummary = {
  total: 0,
  matchable: 0,
  non_matchable: 0,
  missing_email: 0,
  invalid_email: 0,
  missing_phone: 0,
  invalid_phone: 0,
  missing_zip: 0,
  duplicate_email: 0,
  duplicate_phone: 0,
};

function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function defaultAudienceDateFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return ymdLocal(d);
}

export function defaultAudienceDateTo(): string {
  return ymdLocal(new Date());
}

export function useAudienceData(organizationId: string | null, enabled: boolean) {
  const [dateFrom, setDateFrom] = useState(defaultAudienceDateFrom);
  const [dateTo, setDateTo] = useState(defaultAudienceDateTo);
  const [facts, setFacts] = useState<SsdSaleFact[]>([]);
  const [jobs, setJobs] = useState<SsdImportJob[]>([]);
  const [consent, setConsent] = useState<SsdConsentLink[]>([]);
  const [exports, setExports] = useState<SsdExportJob[]>([]);
  const [quality, setQuality] = useState<SsdQualitySummary>(EMPTY_QUALITY);
  const [issues, setIssues] = useState<SsdQualityIssue[]>([]);
  const [feed, setFeed] = useState<SsdFeedState>({ reviewed_through: null, new_count: 0 });
  const [inbound, setInbound] = useState<SsdInboundSnapshot>(EMPTY_INBOUND);
  const [dealerMap, setDealerMap] = useState<SsdDealerMapBoard>(EMPTY_DEALER_MAP);
  const [googleConversions, setGoogleConversions] = useState<SsdGoogleConversions>(EMPTY_CONVERSIONS);
  const [destinationFeeds, setDestinationFeeds] = useState<SsdDestinationFeed[]>([]);
  const [aggregation, setAggregation] = useState<OrgSalesAggregation>(EMPTY_AGGREGATION);
  const [hostedTokens, setHostedTokens] = useState<Partial<Record<"google" | "meta", string>>>({});
  const [savingConversions, setSavingConversions] = useState(false);
  const [rotatingFeed, setRotatingFeed] = useState<"google" | "meta" | null>(null);
  const [loading, setLoading] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [exporting, setExporting] = useState<"google" | "meta" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ingestMessage, setIngestMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!organizationId || !enabled) {
      setFacts([]);
      setJobs([]);
      setConsent([]);
      setExports([]);
      setQuality(EMPTY_QUALITY);
      setIssues([]);
      setFeed({ reviewed_through: null, new_count: 0 });
      setInbound(EMPTY_INBOUND);
      setDealerMap(EMPTY_DEALER_MAP);
      setGoogleConversions(EMPTY_CONVERSIONS);
      setDestinationFeeds([]);
      setAggregation(EMPTY_AGGREGATION);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await fetchSsdSales({ dateFrom, dateTo, limit: 500 });
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      setFacts([]);
      setJobs([]);
      setConsent([]);
      setExports([]);
      setQuality(EMPTY_QUALITY);
      setIssues([]);
      setFeed({ reviewed_through: null, new_count: 0 });
      setInbound(EMPTY_INBOUND);
      setDealerMap(EMPTY_DEALER_MAP);
      setGoogleConversions(EMPTY_CONVERSIONS);
      setDestinationFeeds([]);
      setAggregation(EMPTY_AGGREGATION);
      return;
    }
    setFacts(res.data.facts ?? []);
    setJobs(res.data.jobs ?? []);
    setConsent(res.data.consent ?? []);
    setExports(res.data.exports ?? []);
    setQuality(res.data.quality ?? EMPTY_QUALITY);
    setIssues(res.data.issues ?? []);
    setFeed(res.data.feed ?? { reviewed_through: null, new_count: 0 });
    setInbound(res.data.inbound ?? EMPTY_INBOUND);
    setDealerMap(res.data.dealer_map ?? EMPTY_DEALER_MAP);
    setGoogleConversions(res.data.google_conversions ?? EMPTY_CONVERSIONS);
    setDestinationFeeds(res.data.destination_feeds ?? []);
    setAggregation(res.data.aggregation ?? EMPTY_AGGREGATION);
  }, [organizationId, enabled, dateFrom, dateTo]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const ingestFile = useCallback(
    async (file: File) => {
      if (!organizationId || !enabled) return;
      setIngesting(true);
      setIngestMessage(null);
      setError(null);
      const csvText = await file.text();
      const res = await ingestSsdSalesCsv(csvText, file.name);
      setIngesting(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      const job = res.data.job;
      setIngestMessage(
        job
          ? `${job.filename}: ${job.inserted_count} inserted, ${job.updated_count} updated, ${job.skipped_count} skipped, ${job.issue_count} issues`
          : "Import finished."
      );
      await reload();
    },
    [organizationId, enabled, reload]
  );

  const setShareEndCustomers = useCallback(
    async (relationshipId: string, shareEndCustomers: boolean) => {
      if (!organizationId || !enabled) return false;
      setSharing(true);
      setError(null);
      const res = await patchSsdShareEndCustomers(relationshipId, shareEndCustomers);
      setSharing(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      await reload();
      return true;
    },
    [organizationId, enabled, reload]
  );

  const mapDealer = useCallback(
    async (input: Parameters<typeof mapSsdDealer>[0]) => {
      if (!organizationId || !enabled) return false;
      setMapping(true);
      setError(null);
      const res = await mapSsdDealer(input);
      setMapping(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      await reload();
      return true;
    },
    [organizationId, enabled, reload]
  );

  const exportAudience = useCallback(
    async (destination: "google" | "meta") => {
      if (!organizationId || !enabled) return;
      setExporting(destination);
      setError(null);
      const res = await exportSsdAudienceCsv({
        destination,
        dateFrom,
        dateTo,
        conversionName: destination === "google" ? googleConversions.selected : undefined,
      });
      setExporting(null);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      triggerCsvDownload(res.data.filename, res.data.csv);
      if (res.data.job) setExports((prev) => [res.data.job as SsdExportJob, ...prev]);
    },
    [organizationId, enabled, dateFrom, dateTo, googleConversions.selected]
  );

  const saveGoogleConversion = useCallback(
    async (input: { selected?: string; add?: string }) => {
      if (!organizationId || !enabled) return;
      const optimistic = input.add
        ? {
            names: googleConversions.names.includes(input.add)
              ? googleConversions.names
              : [...googleConversions.names, input.add],
            selected: input.add,
          }
        : { names: googleConversions.names, selected: input.selected ?? googleConversions.selected };
      setGoogleConversions(optimistic);
      setSavingConversions(true);
      setError(null);
      const res = await patchSsdGoogleConversionNames(input);
      setSavingConversions(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      if (res.data.google_conversions) setGoogleConversions(res.data.google_conversions);
    },
    [organizationId, enabled, googleConversions.names, googleConversions.selected]
  );

  const rotateHostedFeed = useCallback(
    async (destination: "google" | "meta") => {
      if (!organizationId || !enabled) return;
      setRotatingFeed(destination);
      setError(null);
      const res = await patchSsdDestinationFeed(destination);
      setRotatingFeed(null);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      if (res.data.feed) {
        setDestinationFeeds((prev) => {
          const rest = prev.filter((f) => f.destination !== destination);
          return [...rest, res.data.feed];
        });
      }
      if (res.data.token) setHostedTokens((prev) => ({ ...prev, [destination]: res.data.token }));
    },
    [organizationId, enabled]
  );

  const acknowledgeNew = useCallback(async () => {
    if (!organizationId || !enabled) return;
    setAcknowledging(true);
    setError(null);
    const res = await acknowledgeSsdNewFacts();
    setAcknowledging(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    await reload();
  }, [organizationId, enabled, reload]);

  return {
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    facts,
    jobs,
    consent,
    exports,
    quality,
    issues,
    feed,
    inbound,
    dealerMap,
    googleConversions,
    destinationFeeds,
    hostedTokens,
    aggregation,
    latestJob: jobs[0] ?? null,
    latestExport: exports[0] ?? null,
    loading,
    ingesting,
    sharing,
    acknowledging,
    mapping,
    exporting,
    savingConversions,
    rotatingFeed,
    error,
    ingestMessage,
    reload,
    ingestFile,
    setShareEndCustomers,
    mapDealer,
    acknowledgeNew,
    exportAudience,
    saveGoogleConversion,
    rotateHostedFeed,
  };
}
